import { useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import z from "zod";

import { GROUP_HEX_COLORS } from "@/constants/promis";
import { PROMIS_SYMPTOMS } from "@/constants/promis";
import { usePromis } from "@/hooks/use-promis";
import { SymptomChart } from "@/routes/_private/promis/-components/symptom-chart";
import type { SegmentedControlOption } from "@/ui";
import {
  Button,
  icons,
  ScreenContent,
  ScreenHeader,
  ScreenTitle,
  SegmentedControl,
  Select,
  Slider,
} from "@/ui";
import { ORIGIN_OPTIONS } from "@/utils";
import { computeTrendLine, formatSymptomName } from "@/utils/promis";

type ChartTab = "score" | "delta";

const TABS: SegmentedControlOption<ChartTab>[] = [
  { value: "score", label: "Raw score" },
  { value: "delta", label: "Δ from baseline" },
];

const SymptomDetailPage = () => {
  const { "symptom-code": symptomCodeParam } = Route.useParams();
  const symptomCode = symptomCodeParam;
  const { from, referredBy, to } = Route.useSearch();
  const [activeTab, setActiveTab] = useState<ChartTab>("score");
  const navigate = Route.useNavigate();

  const { data: allPoints } = usePromis().getSymptomMetricPoints(symptomCode, { from, to });

  const {
    data: chartPoints,
    isError,
    isLoading,
  } = usePromis().getSymptomMetricPoints(symptomCode, { from, to, referredBy });

  const filteredPoints = useMemo(() => {
    return chartPoints ?? [];
  }, [chartPoints]);

  const trendData = useMemo(() => {
    return computeTrendLine(filteredPoints);
  }, [filteredPoints]);

  const maxDaysRef = useRef<number | undefined>(undefined);

  const hasFilters = referredBy !== undefined || from !== undefined || to !== undefined;
  const symptomColor = GROUP_HEX_COLORS[symptomCode] ?? "#6366f1";

  const maxDays = useMemo(() => {
    if (!allPoints || allPoints.length === 0) {
      return maxDaysRef.current;
    }

    const computed = Math.max(
      ...allPoints.map((p) => {
        return p.daysSinceBaseline;
      }),
    );

    if (maxDaysRef.current === undefined || computed > maxDaysRef.current) {
      maxDaysRef.current = computed;
    }

    return maxDaysRef.current;
  }, [allPoints]);

  return (
    <>
      <ScreenHeader>
        <div className="flex flex-col gap-2">
          <Link
            className="flex items-center gap-1 text-sm text-content-muted hover:text-content"
            to="/promis"
          >
            <span aria-hidden="true">&larr;</span> Back
          </Link>
          <ScreenTitle>{formatSymptomName(symptomCode)}</ScreenTitle>
        </div>
      </ScreenHeader>

      <ScreenContent>
        <div className="rounded-lg border border-line bg-surface p-6">
          <div className="flex flex-col items-start justify-end gap-8 2xl:flex-row">
            <div className="mr-auto ml-0 shrink-0">
              <p className="text-sm font-semibold">Patient scores over time</p>
              <p className="text-xs">Hover a point for details</p>
            </div>

            {/* Filters */}
            <div className="flex w-full flex-col items-start gap-4 xl:flex-row">
              {maxDays !== undefined && (
                <div className="flex w-full flex-1 flex-col gap-2">
                  <p className="text-sm font-semibold">
                    Tracking window (
                    {from === undefined && to === undefined
                      ? "All time"
                      : `Day ${from ?? 0} – Day ${to ?? maxDays}`}
                    )
                  </p>
                  <Slider
                    className="w-full xl:min-w-96"
                    defaultValue={[from ?? 0, to ?? maxDays]}
                    formatLabel={(v) => {
                      return String(v);
                    }}
                    key={`${from ?? 0}-${to ?? maxDays}`}
                    max={maxDays}
                    min={0}
                    onValueCommit={(values) => {
                      const [newFrom, newTo] = values;
                      const isFullRange = newFrom === 0 && newTo === maxDays;
                      void navigate({
                        search: (prev) => {
                          return {
                            ...prev,
                            from: isFullRange ? undefined : newFrom,
                            to: isFullRange ? undefined : newTo,
                          };
                        },
                      });
                    }}
                    step={1}
                  />
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Select
                  emptyOptionLabel="All referrers"
                  id="referred-by-filter"
                  label="Referred by"
                  onChange={(e) => {
                    void navigate({
                      search: (prev) => {
                        return {
                          ...prev,
                          referredBy: e.target.value || undefined,
                        };
                      },
                    });
                  }}
                  options={ORIGIN_OPTIONS}
                  value={referredBy ?? ""}
                />
              </div>
            </div>

            {/* A filter group, not a navigation landmark. */}
            <div className="flex flex-wrap items-center gap-2">
              <SegmentedControl
                label="Chart series"
                onChange={setActiveTab}
                options={TABS}
                value={activeTab}
              />

              <Button
                className="py-1.5 whitespace-nowrap"
                disabled={!hasFilters}
                onClick={() => {
                  void navigate({
                    to: ".",
                    params: {
                      "symptom-code": symptomCode,
                    },
                  });
                }}
                type="button"
                variant="tertiaryFilled"
              >
                Reset filters
              </Button>
            </div>
          </div>

          <div className="pt-6">
            {isLoading ? (
              <div
                aria-live="polite"
                className="flex h-105 items-center justify-center"
                role="status"
              >
                <icons.SpinnerIcon />

                <span className="sr-only">Loading chart data…</span>
              </div>
            ) : null}

            {isError ? (
              <div className="flex h-105 items-center justify-center text-sm text-danger-content">
                Failed to load data. Please try again.
              </div>
            ) : null}

            {!isLoading && !isError && filteredPoints.length === 0 && (
              <div className="flex h-105 items-center justify-center text-sm text-content-muted">
                {hasFilters
                  ? "No data for the selected filters."
                  : `No data available for ${formatSymptomName(symptomCode)}.`}
              </div>
            )}

            {!isLoading && !isError && filteredPoints.length > 0 ? (
              <>
                {activeTab === "score" && (
                  <SymptomChart
                    color={symptomColor}
                    points={filteredPoints}
                    trendData={trendData}
                    trendYKey="avgPromisScore"
                    yKey="promisScore"
                    yLabel="PROMIS Score"
                    yTickStep={10}
                  />
                )}
                {activeTab === "delta" && (
                  <SymptomChart
                    color={symptomColor}
                    points={filteredPoints}
                    trendData={trendData}
                    trendYKey="avgDeltaFromBaseline"
                    yKey="deltaFromBaseline"
                    yLabel="Change from Baseline"
                    yTickStep={5}
                  />
                )}
              </>
            ) : null}
          </div>
        </div>
      </ScreenContent>
    </>
  );
};

export const Route = createFileRoute("/_private/promis/$symptom-code/")({
  component: SymptomDetailPage,
  params: {
    parse: (params: Record<string, string>) => {
      return {
        "symptom-code": z.enum(PROMIS_SYMPTOMS).parse(params["symptom-code"].replaceAll("-", "_")),
      };
    },
    stringify: (params: { "symptom-code": string }) => {
      return { "symptom-code": params["symptom-code"].replaceAll("_", "-") };
    },
  },
  validateSearch: z.object({
    from: z.coerce.number().optional(),
    to: z.coerce.number().optional(),
    referredBy: z.string().optional(),
  }),
  errorComponent: () => {
    return (
      <div className="flex h-full items-center justify-center text-sm text-content-muted">
        Invalid symptom. Valid options: {PROMIS_SYMPTOMS.join(", ")}.
      </div>
    );
  },
});
